import type { Stage } from '../../api/types'
import { summaryCounts } from './grouping'
import styles from './SummaryTiles.module.css'

/**
 * Four counts over the whole filtered set, from the server, keyed by fields the
 * backend returned. "In pipeline" is the approved presentation grouping declared
 * in ./grouping.ts — it groups backend stages, it does not decide any deal's
 * stage.
 */
export function SummaryTiles({
  countsByStage,
  reEvaluationCount,
  activeJobCount,
  jobsUnavailable,
}: {
  countsByStage: Partial<Record<Stage, number>>
  reEvaluationCount: number
  activeJobCount: number
  jobsUnavailable: boolean
}) {
  const counts = summaryCounts(countsByStage, reEvaluationCount, activeJobCount)

  // "In pipeline" leads, as in the redesign: it is the number the user watches,
  // and Qualified is read against it rather than the other way round.
  const tiles = [
    { label: 'In pipeline', value: String(counts.inPipeline), note: 'advancing' },
    { label: 'Qualified', value: String(counts.qualified), note: 'double down' },
    { label: 'Re-evaluation needed', value: String(counts.needsReEvaluation), note: 'a new call changed the verdict' },
    {
      label: 'Active analyses',
      // Unavailable is not zero. Saying "0" when the request failed is the
      // conflation this rewrite is meant to end.
      value: jobsUnavailable ? '—' : String(counts.activeAnalyses),
      note: jobsUnavailable ? 'could not be loaded' : 'processing now',
    },
  ]

  return (
    <ul className={styles.tiles} aria-label="Pipeline summary">
      {tiles.map((tile) => (
        <li key={tile.label} className={styles.tile}>
          <span className={styles.value}>{tile.value}</span>
          <span className={styles.label}>{tile.label}</span>
          <span className={styles.note}>{tile.note}</span>
        </li>
      ))}
    </ul>
  )
}
